import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import applyImageOpacityToSelectionOperation from 'editor/src/store/design/operation/applyImageOpacityToSelectionOperation';
import getSelectedMediaElementsObjects from 'editor/src/store/design/selector/getSelectedMediaElementsObjects';
import { MediaImage } from 'editor/src/store/design/types';
import { getImageOpacity } from 'editor/src/store/design/util/imageOpacity';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import { useLiveUpdateMediaElement } from 'editor/src/util/useMediaElementLiveUpdates';

import SliderWithInput from 'editor/src/component/SliderWithInput';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

/** GAPI-61889: 0–100 % opacity slider for the selected image. Dragging previews live; releasing stores one undo step. */
function ImageOpacityControl() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  // every selected image: the slider previews and commits the same opacity on all of them
  const images = useSelector(
    (state) =>
      getSelectedMediaElementsObjects(state).filter((element): element is MediaImage => element.type === 'image'),
    shallowEqual,
  );
  const image = images[0];
  const liveUpdate = useLiveUpdateMediaElement();

  const onChange = useCallback(
    (percent: number) => {
      images.forEach((selectedImage) => liveUpdate({ ...selectedImage, opacity: percent / 100 }));
    },
    [images, liveUpdate],
  );

  const onAfterChange = useCallback((percent: number) => {
    dispatch(applyImageOpacityToSelectionOperation(percent / 100));
  }, []);

  if (!image) {
    return null;
  }

  return (
    <SliderWithInput
      className="cy-image-opacity-control"
      inputClassName="cy-image-opacity-input"
      Label={t('editor-opacity')}
      onChange={onChange}
      onAfterChange={onAfterChange}
      min={0}
      max={100}
      step={1}
      startPoint={0}
      value={Math.round(getImageOpacity(image) * 100)}
      position={isMobile ? 'twoRows' : 'singleRow'}
    />
  );
}

export default React.memo(ImageOpacityControl);
